/**
 * EID-104: pure helper narrowing a transform watcher's data dependency to
 * only its own declared inputs.
 *
 * startTransformWatcher (data-provider.tsx) previously keyed its effect on
 * the whole `data` registry signal (`on([data], ...)`), so ANY data-registry
 * change — an unrelated datasource refreshing, another transform's output
 * landing — re-ran the effect and terminated+recreated this transform's
 * worker via Transform.init. Keying the effect on a memo of this transform's
 * own input identities (gated by sameInputIdentities) means it now only
 * re-fires when one of ITS OWN inputs actually changed value.
 *
 * data() itself is a plain signal (not a store), but setData always spreads
 * the previous object (`{ ...prev, [id]: wrapped }`), so untouched entries
 * keep the same reference across updates — sameInputIdentities can therefore
 * gate on simple referential equality per input.
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note.
 */

/**
 * Read a transform's own input values, in declaration order, out of the data
 * registry. Used as the createMemo selector.
 */
export function readOwnInputs(
  inputs: readonly string[],
  data: Record<string, unknown>,
): unknown[] {
  return inputs.map((input) => data[input]);
}

/**
 * Equality comparator for createMemo({ equals }): two input-value arrays are
 * equal when they have the same length and every value is referentially
 * identical, in the same order.
 */
export function sameInputIdentities(a: unknown[], b: unknown[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
